/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import { getCurrentDateObject, getDateObj, getNextDateObject } from '@marriott/mi-ui-library';
import {
  SPECIAL_RATES,
  DATES,
  DESTINATION,
  ROOMS_AND_GUESTS,
  USE_POINTS,
  DESTINATION_HIDDEN_FIELDS,
  LATITUDE_AND_LONGITUDE,
  BRANDS,
  ERROR_MESSAGE,
  MEETINGS_DESTINATION,
  MEETINGS_DATES,
  EVENTS_DESTINATION_HIDDEN_FIELDS,
  EVENT_PURPOSE,
  GUEST_ROOMS,
  EVENT_SPACE,
  DEFAULT_CLUSTER_CODE,
} from '../../modules/store/store.constants';
import { JP_LOCALE, KR_LOCALE, MAX_NUMBER_OF_NIGHTS_ALLOWED } from '../constants/Calendar.constants';
import { ADF_ROOM_COUNT_MAP } from '../../lib/organisms/SearchForm/constants/StoreConstants';
import { isCNLocale } from '@marriott/shared/mi-helper-utils';
import { CONCUR_AFFILIATE_KEY } from '../constants';
import {
  baselineSearchFormClickTrackingLoc,
  DATALAYER_CONSTANT,
  nextGenSearchFormClickTrackingLoc,
  searchFormClickTrackingLoc,
} from '../../constants/lib/constants';
import { KeysRequest } from '../../utils/src/dataLayer/index.types';
import Axios from 'axios';
import { getLanguage } from '../../utils/src/subDirectoryFunctions';

import { TAGP_CODES } from '../../constants/lib/constants';

export const isTAGPCodes = (corporateCode?: string, restrictRoomCount?: string) =>
  Object.keys(TAGP_CODES).includes(corporateCode?.toUpperCase() ?? '') && Boolean(restrictRoomCount);

export const maxRoomCountForTA = (corporateCode?: string) =>
  TAGP_CODES[(corporateCode?.toUpperCase() as keyof typeof TAGP_CODES) ?? ''] ?? '';

export const getRoomCountForTA = (
  roomsCount: string,
  corporateCode: string,
  restrictRoomCount: string
): { roomQuantity: string } => {
  // to guard against invalid input
  if (!roomsCount || !corporateCode || !restrictRoomCount) {
    return { roomQuantity: roomsCount || '1' };
  }

  const roomStr = roomsCount.toString();
  const parts = roomStr.split(/[-~+]/);
  const count = parts.length > 0 ? Number(parts[0]) : 1;

  // Validate count is a valid number
  if (isNaN(count)) {
    console.warn('Invalid room count:', roomsCount);
    return { roomQuantity: '1' };
  }

  const maxQty = maxRoomCountForTA(corporateCode);
  if (isTAGPCodes(corporateCode, restrictRoomCount) && count > Number(maxQty)) {
    return { roomQuantity: maxQty };
  }
  return { roomQuantity: roomsCount };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDestinationText = (searchCriteria: any) => {
  const { destinationAddressMainText, destinationAddressSecondaryText, address, airportCode, poiName } = searchCriteria;
  if (address?.destination) {
    return address?.destination;
  } else if (destinationAddressMainText && destinationAddressSecondaryText) {
    return `${destinationAddressMainText}, ${destinationAddressSecondaryText}`;
  } else {
    return `${airportCode}, ${poiName}`;
  }
};

export const getMeetingsAndEventsDestinationText = (groupSearchCriteria: { address: any }) => {
  const { address } = groupSearchCriteria;
  if (address?.destination) {
    return address?.destination;
  } else if (address?.destinationAddressMainText && address?.destinationAddressSecondaryText) {
    return `${address.destinationAddressMainText}, ${address.destinationAddressSecondaryText}`;
  }
};

export const invalidDates = (search_date_check_in: string, search_date_check_out: string) => {
  // Function to check if a date string is valid
  const isValidDate = (dateString: any) => {
    return !isNaN(Date?.parse(dateString));
  };

  // Extract fromDate and toDate from the session
  const fromDate = search_date_check_in;
  const toDate = search_date_check_out;
  const bothDatesNullOrEmpty = (fromDate === null || fromDate === '') && (toDate === null || toDate === '');

  // Check if both fromDate and toDate are present in the URL
  if (fromDate !== null && toDate !== null) {
    // Check if the dates are invalid
    const fromDateInvalid = !isValidDate(fromDate);
    const toDateInvalid = !isValidDate(toDate);
    // Set a flag based on the validity of fromDate and toDate
    const invalidDatesFlag = fromDateInvalid || toDateInvalid;
    // Trigger the function only if there are invalid dates
    if (invalidDatesFlag) {
      return invalidDatesFlag;
    }
  } else if (bothDatesNullOrEmpty) {
    return true;
  }
  return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initializeSearchForm(data: any, modelData: any): any {
  const { AriesSearch, AriesCommon, AriesGroupSearch, AriesReservation } = data || {};
  let sessionObj = {};
  if (!AriesSearch && !AriesGroupSearch) {
    return sessionObj;
  }

  const currentDate = getCurrentDateObject();
  const nextDate = getNextDateObject(currentDate);
  // Read the group search form session data for Meetings and Events searchform
  if (AriesGroupSearch) {
    const { groupSearchCriteria } = AriesGroupSearch;
    const {
      address: meetingsAndEventsAddress,
      checkInDate: meetingsAndEventsCheckInDate,
      checkOutDate: meetingsAndEventsCheckOutDate,
      lengthOfStay: meetingsAndEventsLengthOfStay,
      isFlexibleDate: meetingsAndEventsIsFlexibleDate,
      eventSearchType,
      guestRoomCount,
      sizeLargestMeetingRoom,
    } = groupSearchCriteria;

    let meetingsAndEventsFlexible;
    let meetingsAndEventsFromDate;
    let meetingsAndEventsToDate;
    let meetingsAndEventsNumOfNights;

    /*
        If check-in and check-out dates are missing and flexibleDates flag is false,
        then it will be considered as 1 night flexible dates flow for current month
      */
    if (
      !meetingsAndEventsCheckInDate &&
      !meetingsAndEventsCheckOutDate &&
      meetingsAndEventsIsFlexibleDate === 'false'
    ) {
      meetingsAndEventsFlexible = true;
      meetingsAndEventsFromDate = currentDate;
      meetingsAndEventsToDate = nextDate;
      meetingsAndEventsNumOfNights = 1;
    } else {
      meetingsAndEventsFlexible = meetingsAndEventsIsFlexibleDate;
      meetingsAndEventsFromDate = getDateObj(meetingsAndEventsCheckInDate);
      meetingsAndEventsToDate = getDateObj(meetingsAndEventsCheckOutDate);
      meetingsAndEventsNumOfNights = meetingsAndEventsIsFlexibleDate
        ? meetingsAndEventsLengthOfStay > MAX_NUMBER_OF_NIGHTS_ALLOWED
          ? 1
          : meetingsAndEventsLengthOfStay
        : meetingsAndEventsLengthOfStay;
    }

    sessionObj = {
      [ERROR_MESSAGE]: {
        errorMessages: '',
      },
      [MEETINGS_DESTINATION]: {
        eventsDisplayText: getMeetingsAndEventsDestinationText(groupSearchCriteria),
        eventsDestinationAddressPlaceId: groupSearchCriteria?.destinationAddressPlaceId,
      },
      [MEETINGS_DATES]: {
        eventsFromDate: meetingsAndEventsFromDate,
        eventsToDate: meetingsAndEventsToDate,
        flexible: meetingsAndEventsFlexible,
        numberOfNights: meetingsAndEventsNumOfNights,
      },
      [EVENTS_DESTINATION_HIDDEN_FIELDS]: {
        latitude: meetingsAndEventsAddress?.latitude,
        longitude: meetingsAndEventsAddress?.longitude,
        destinationAddressMainText: meetingsAndEventsAddress?.destinationAddressMainText,
        destinationAddressSecondaryText: meetingsAndEventsAddress?.destinationAddressSecondaryText,
        city: meetingsAndEventsAddress?.city,
        state: meetingsAndEventsAddress?.stateProvince,
        country: meetingsAndEventsAddress?.country,
        destinationType: meetingsAndEventsAddress?.destinationType,
      },
      [EVENT_PURPOSE]: {
        purpose: eventSearchType,
      },
      [GUEST_ROOMS]: {
        noOfRooms: guestRoomCount,
      },
      [EVENT_SPACE]: {
        noOfAttendees: sizeLargestMeetingRoom,
      },
    };
  }

  if (AriesSearch) {
    let invalidDate: any = '';
    const search_date_check_in = AriesCommon?.search_date_check_in;
    const search_date_check_out = AriesCommon?.search_date_check_out;

    const { searchCriteria } = AriesSearch;
    const { styleclass } = modelData;
    let errorMessages: any = '';
    if (AriesSearch && AriesSearch?.errorMessages && styleclass) {
      errorMessages = AriesSearch?.errorMessages;
      invalidDate = invalidDates(search_date_check_in, search_date_check_out);
    }

    const rk_clusterCode = AriesCommon?.rk_clusterCode;
    const isRewardRedemption = AriesCommon?.isRewardRedemption;
    const search_isFlexibleDate = AriesCommon?.search_isFlexibleDate;
    const clustCode = AriesCommon?.clustCode;
    const res_num_adults = AriesCommon?.res_num_adults;
    const res_num_children = AriesCommon?.res_num_children;

    let fromDate;
    let toDate;
    let flexible;
    let numOfNights;

    if (search_isFlexibleDate === true || search_isFlexibleDate === 'true') {
      flexible = true;
      fromDate = search_date_check_in && getDateObj(new Date(search_date_check_in));
      toDate = search_date_check_out && getDateObj(new Date(search_date_check_out));
      numOfNights = AriesCommon?.lengthOfStay;
    } else if (
      search_date_check_in &&
      search_date_check_out &&
      (search_isFlexibleDate === false || search_isFlexibleDate === 'false')
    ) {
      if (invalidDate) {
        flexible = false;
        fromDate = null;
        toDate = null;
        numOfNights = null;
      } else {
        flexible = false;
        fromDate = search_date_check_in && getDateObj(new Date(search_date_check_in));
        toDate = search_date_check_out && getDateObj(new Date(search_date_check_out));
        numOfNights = AriesCommon?.lengthOfStay;
      }
    } else if (AriesSearch && AriesSearch?.errorMessages && invalidDate) {
      flexible = false;
      fromDate = null;
      toDate = null;
      numOfNights = null;
    }

    let numbRooms = '';
    const { roomListLabel } = modelData;
    const tempArr = roomListLabel?.split(', ');
    const roomCounts = tempArr?.filter((item: string) => {
      return item?.includes(AriesCommon?.numRooms);
    });
    if (AriesCommon?.numRooms) {
      if (AriesCommon?.numRooms === 4) {
        numbRooms = roomCounts?.length ? roomCounts[0] : '4-9';
      } else if (AriesCommon?.numRooms === 10) {
        numbRooms = roomCounts?.length ? roomCounts[0] : '10-25';
      } else if (AriesCommon?.numRooms === 26) {
        numbRooms = roomCounts?.length ? roomCounts[0] : '26+';
      } else {
        numbRooms = AriesCommon?.numRooms;
      }
    }
    let corporate_code = '';
    if (
      rk_clusterCode &&
      (rk_clusterCode === 'CORP' || rk_clusterCode === 'GOV' || rk_clusterCode === 'AAA' || rk_clusterCode === 'NONE')
    ) {
      corporate_code = rk_clusterCode.toLowerCase();
    } else {
      corporate_code = rk_clusterCode ? rk_clusterCode : 'none';
    }
    let rewardsRed = false;
    if (isRewardRedemption && isRewardRedemption === 'true') {
      rewardsRed = true;
    } else {
      rewardsRed = false;
    }

    // Check if searchCriteria is present in session document
    if (AriesSearch && AriesSearch?.errorMessages && AriesCommon) {
      let destination = AriesSearch?.search_keyword && AriesSearch?.search_keyword;
      let locationBlank = true;
      if (
        errorMessages?.submitSearchFormErrorMessages?.errorMessageKeys[0] ===
        'search-error-messages|location.blank.invalid'
      ) {
        destination = '';
        locationBlank = false;
      }
      if (!AriesSearch?.search_keyword) {
        if (locationBlank) {
          return (sessionObj = {
            ...sessionObj,
            [ERROR_MESSAGE]: {
              errorMessages: errorMessages,
            },
          });
        } else {
          return (sessionObj = {
            ...sessionObj,
            [ERROR_MESSAGE]: {
              errorMessages: errorMessages,
            },
            [DESTINATION]: {
              displayText: destination,
            },
            [DATES]: {
              lastDefaultDate: search_date_check_in,
              fromDate,
              toDate,
              flexible,
              search_date_check_in,
              search_date_check_out,
              lengthOfStay: numOfNights,
              numberOfNights: numOfNights,
            },
          });
        }
      } else if (
        AriesSearch &&
        AriesSearch?.errorMessages &&
        rk_clusterCode &&
        search_date_check_in &&
        res_num_adults &&
        AriesSearch?.search_keyword
      ) {
        sessionObj = {
          ...sessionObj,
          [ERROR_MESSAGE]: {
            errorMessages: errorMessages,
          },
          [DESTINATION]: {
            displayText: destination,
          },
          [DATES]: {
            lastDefaultDate: search_date_check_in,
            fromDate,
            toDate,
            flexible,
            search_date_check_in,
            search_date_check_out,
            lengthOfStay: numOfNights,
            numberOfNights: numOfNights,
          },
          [ROOMS_AND_GUESTS]: {
            numRooms: numbRooms,
            numAdultsPerRoom: res_num_adults,
            numChildrenPerRoom: res_num_children ? res_num_children : 0,
            childrenAges: res_num_children ? (AriesCommon?.childrenAges ? AriesCommon?.childrenAges : []) : [],
          },
          [USE_POINTS]: {
            rewardsRedemption: rewardsRed,
          },
          [SPECIAL_RATES]: {
            clusterCode: rk_clusterCode?.toLocaleLowerCase(),
            specialRateCode: corporate_code,
            corporateCode: clustCode ? clustCode : '',
          },
          [BRANDS]: {
            marriottBrands: AriesCommon?.brandCodes ? AriesCommon?.brandCodes : '',
          },
        };
        if (!AriesGroupSearch) {
          sessionObj = {
            ...sessionObj,
            [MEETINGS_DESTINATION]: {
              eventsDisplayText: destination,
            },
            [MEETINGS_DATES]: {
              eventsLastDefaultDate: search_date_check_in,
              eventsFromDate: fromDate,
              eventsToDate: toDate,
              numberOfNights: numOfNights,
            },
          };
        }
        return sessionObj;
      } else if (AriesSearch && AriesSearch?.errorMessages) {
        return (sessionObj = {
          ...sessionObj,
          [ERROR_MESSAGE]: {
            errorMessages: errorMessages,
          },
        });
      } else {
        return sessionObj;
      }
    } else if (AriesSearch && !AriesSearch?.errorMessages && !AriesSearch?.searchCriteria) {
      return (sessionObj = {
        ...sessionObj,
        [ERROR_MESSAGE]: {
          errorMessages: '',
        },
      });
    }
    const {
      availabilityRequestVO,
      destinationAddressPlaceId,
      keywordSearchRequestVO,
      searchType,
      address,
      destinationAddressMainText,
      destinationAddressSecondaryText,
      searchRadius,
      brandCodes,
      propertyId,
    } = searchCriteria || {};
    const {
      checkInDate,
      checkOutDate,
      flexibleDate,
      lengthOfStay,
      numRooms,
      numAdultsPerRoom,
      numChildrenPerRoom,
      childrenAges,
      rewardsRedemption,
      clusterCode: searchClusterCode,
      corporateCode: searchCorporateCode,
      groupCode: searchGroupCode,
    } = availabilityRequestVO || {};
    const {
      clusterCode: reservationClusterCode,
      corporateCode: reservationCorporateCode,
      groupCode: reservationGroupCode,
    } = AriesReservation?.availabilityRequestVO || {};
    const clusterCode = searchClusterCode || reservationClusterCode;
    const corporateCode = searchCorporateCode || reservationCorporateCode;
    const groupCode = searchGroupCode || reservationGroupCode;
    let specialRateCode = clusterCode;
    /* istanbul ignore else */
    if (specialRateCode === 'AAA' || specialRateCode === 'GOV' || specialRateCode === 'NONE') {
      specialRateCode = specialRateCode?.toLocaleLowerCase();
    }
    // For GROUP rate: clusterCode='GROUP', specialRateCode='group', groupCode=groupCode
    if (specialRateCode?.toUpperCase() === 'GROUP') {
      specialRateCode = 'group';
    }
    let latitude;
    let longitude;

    /* istanbul ignore else */
    if (address?.latitude && address?.longitude) {
      latitude = address?.latitude;
      longitude = address?.longitude;
    }

    /*
        If check-in and check-out dates are missing and flexibleDates flag is false,
        then it will be considered as 1 night flexible dates flow for current month
      */
    if (!checkInDate && !checkOutDate && flexibleDate === false) {
      flexible = true;
      fromDate = currentDate;
      toDate = nextDate;
      numOfNights = 1;
    } else {
      flexible = flexibleDate;
      fromDate = getDateObj(checkInDate);
      toDate = getDateObj(checkOutDate);
      numOfNights = flexibleDate ? (lengthOfStay > MAX_NUMBER_OF_NIGHTS_ALLOWED ? 1 : lengthOfStay) : lengthOfStay;
    }

    if ((destinationAddressPlaceId || address?.destination) && !AriesSearch?.errorMessages) {
      sessionObj = {
        ...sessionObj,
        [DESTINATION]: {
          displayText: getDestinationText(searchCriteria),
          destinationAddressPlaceId: destinationAddressPlaceId,
        },
        [DATES]: {
          lastDefaultDate: checkInDate,
          fromDate,
          toDate,
          flexible,
          checkInDate,
          checkOutDate,
          lengthOfStay: numOfNights,
          numberOfNights: numOfNights,
        },
        [ROOMS_AND_GUESTS]: {
          numRooms: numRooms,
          numAdultsPerRoom: numAdultsPerRoom,
          numChildrenPerRoom: numChildrenPerRoom,
          childrenAges: numChildrenPerRoom ? childrenAges : [],
        },
        [DESTINATION_HIDDEN_FIELDS]: {
          latitude: latitude,
          longitude: longitude,
          destinationAddressMainText: destinationAddressMainText,
          destinationAddressSecondaryText: destinationAddressSecondaryText,
          city: address?.city,
          state: address?.stateProvince,
          country: address?.country,
          searchRadius: searchRadius,
          recentlyViewedPopertyCode: propertyId,
          destinationType: address?.destinationType,
          region: address?.region,
        },
        [LATITUDE_AND_LONGITUDE]: {
          latitude: latitude,
          longitude: longitude,
        },
        [USE_POINTS]: {
          rewardsRedemption: rewardsRedemption,
        },
        [BRANDS]: {
          marriottBrands: brandCodes,
        },
        [SPECIAL_RATES]: {
          clusterCode: clusterCode || DEFAULT_CLUSTER_CODE,
          specialRateCode: specialRateCode || DEFAULT_CLUSTER_CODE,
          corporateCode: corporateCode || '',
          groupCode: groupCode || '',
        },
        [ERROR_MESSAGE]: {
          errorMessages: '',
        },
      };
    }

    if (searchType === 'Keyword' && !AriesSearch?.errorMessages) {
      sessionObj = {
        ...sessionObj,
        [DESTINATION]: {
          displayText: `${keywordSearchRequestVO?.keywords}`,
        },
        [DATES]: {
          flexibleDate: true,
        },
        [USE_POINTS]: {
          rewardsRedemption: rewardsRedemption,
        },
        [BRANDS]: {
          marriottBrands: brandCodes,
        },
        [SPECIAL_RATES]: {
          clusterCode: clusterCode || DEFAULT_CLUSTER_CODE,
          specialRateCode: DEFAULT_CLUSTER_CODE,
          corporateCode: corporateCode || '',
          groupCode: groupCode || '',
        },
        [ERROR_MESSAGE]: {
          errorMessages: '',
        },
      };
    }
  }

  return sessionObj;
}

export function searchFormHelperFunction(
  state: Record<string, any>,
  fieldNames: Array<string>,
  fieldsData: Record<string, any>,
  reset?: boolean
): object {
  const updatedState = state;
  //to reset everything inside the given field , this will not reset the whole store but only reset the given fieldname
  if (reset) {
    fieldNames.forEach((name: string) => {
      updatedState[name] = {};
    });
  }
  fieldNames.forEach((name: string) => {
    updatedState[name] = { ...updatedState[name], ...fieldsData[name] };
  });
  return updatedState;
}

// Removing the duplicate keys in URL
export function removeDuplicateAndNullKeys(queryString: any) {
  const params = new URLSearchParams(queryString);
  // Prepare a new URLSearchParams object for unique parameters
  const uniqueParams = new URLSearchParams();
  const dupKeys = new Set();

  for (const [key, value] of params.entries()) {
    const isInvalidValue = value === undefined || value === null || value === 'undefined' || value === 'null';

    if (isInvalidValue) continue;

    if (!dupKeys.has(key)) {
      uniqueParams.append(key, value);
      dupKeys.add(key);
    }
  }
  return uniqueParams.toString();
}

/* istanbul ignore next */
export const constructSearchformLabels = (model: any) => {
  if (model) {
    const isTabbedSearchForm =
      typeof model?.['styleclass'] === 'string' && model?.['styleclass'].toLowerCase() === 'phoenix' ? true : false;

    model['isTabbedSearchForm'] = isTabbedSearchForm;
    model['submitAction'] = model['submitAction'] ?? 'default';

    model['submitSpecificDateCta'] = model['submitSpecificDateCta'] ?? model['dateModuleSubmitCta'];
    model['submitSpecificDateCtaMobile'] =
      model['submitSpecificDateCtaMobile'] ?? model['dateModuleSubmitCta'] ?? 'Next';
    model['flexibleDatesSubmitCta'] = model['flexibleDatesSubmitCta'] ?? model['flexibleSearchSubmitCTA'];
    model['resetSpecificDateCta'] = model['resetSpecificDateCta'] ?? model['dateModuleResetCta'];
    model['flexibleDatesResetCta'] = model['flexibleDatesResetCta'] ?? model['reset'];

    model['enableWeekendSelectorOnMobile'] = model['enableWeekendSelectorOnMobile'] ?? model['enableWeekendSelector'];
    model['dateModalHeader'] = model['dateModalHeader'] ?? model['stayDates'];
    model['destinationModalHeader'] = model['destinationModalHeader'] ?? model['placeholderTextMobile'];

    model['nameOfRecentlyViewedSection'] =
      model['nameOfRecentlyViewedSection'] ?? model['placeholderTextrecentlyViewedLabel'];
    model['nameOfRecentSearchesSection'] =
      model['nameOfRecentSearchesSection'] ?? model['placeholderTextRecentSearchesLabel'];
    model['nameOfPopularDestinationSection'] =
      model['nameOfPopularDestinationSection'] ?? model['trendingDestinationsLabel'];
    model['nightLabel'] = model['nightLabel'] ?? model['night'];
    model['night'] = model['night'] ?? model['nightLabel'];
    model['roomsAndGuestEyebrowText'] = model['roomsAndGuestEyebrowText'] ?? model['roomsAndGuestEybrowText'];
    model['placeholderTextMobile'] = model['placeholderTextMobile'] ?? model['expandedHelperTextMobile'];
    model['findHotelLabel'] = model['findHotelLabel'] ?? model['submitCTA'];
  }
};
//function to check if recent view and recent searches are available on localstorgae
export const isRecentViewAndSearchAvailable = (isServer: any, currentLocale: string) => {
  const getRecentSearchList: string | null = !isServer ? localStorage.getItem('miRecentSearch') : null;
  const recentSearchesAll: Array<Record<string, string>> = getRecentSearchList && JSON.parse(getRecentSearchList);
  //this function will fetch the last 3 recently searched destinations from localstorage
  const recentSearches: Array<Record<string, string>> = recentSearchesAll?.slice(-3);
  if (recentSearches && recentSearches.length > 0) {
    const nonPastDateSearches = recentSearches.filter(item => {
      //converting date to hours(0,0,0,0) to avoid time comparision , only dates are being considered
      return new Date(item['fromDate']).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
    });
    if (nonPastDateSearches && nonPastDateSearches.length) {
      const nonFlexibleSearches = nonPastDateSearches;
      // added JP and KR locale condition as we want the condition to be false for these 2 locales here and show it on CityPickList
      if (
        nonFlexibleSearches?.length &&
        currentLocale !== JP_LOCALE &&
        currentLocale !== KR_LOCALE &&
        !isCNLocale(currentLocale)
      ) {
        return true;
      }
    }
  }
  const getRecentViewList: string | null = !isServer ? localStorage.getItem('miRecentlyViewedProperties') : null;
  const recentlyViewedAll: Array<Record<string, string>> =
    getRecentViewList && JSON.parse(getRecentViewList)?.recentlyViewedProperties;
  //this function will fetch the last 3 recently viewed properties from localstorage
  const recentlyViewed: Array<Record<string, string>> = recentlyViewedAll?.slice(0, 3);
  // added JP and KR locale condition as we want the condition to be false for these 2 locales here and show it on CityPickList
  if (
    recentlyViewed &&
    recentlyViewed.length > 0 &&
    currentLocale !== JP_LOCALE &&
    currentLocale !== KR_LOCALE &&
    !isCNLocale(currentLocale)
  ) {
    return true;
  }
  return false;
};

export const getRoomsCountDisplayValue = (roomCount?: number) => {
  if (roomCount) {
    if (roomCount >= 26) {
      roomCount = 26;
    } else if (Number(roomCount) >= 10 && Number(roomCount) <= 25) {
      roomCount = 10;
    } else if (Number(roomCount) >= 4 && Number(roomCount) <= 9) {
      roomCount = 4;
    }
  } else {
    roomCount = 1;
  }
  return roomCount <= 3 ? roomCount.toString() : ADF_ROOM_COUNT_MAP[roomCount];
};

export const checkIfValidRoomCountValue = (roomsList: Array<{ roomQuantity: string }>, roomCount?: string) => {
  if (!roomCount) {
    roomCount = '1';
  }
  const roomCountDisplayValue = getRoomsCountDisplayValue(parseInt(roomCount));

  let isValidRoomCountValue = false;
  roomsList.forEach(roomEntry => {
    if (roomEntry.roomQuantity === roomCountDisplayValue) {
      isValidRoomCountValue = true;
    }
  });
  return isValidRoomCountValue;
};

export const convertStateValuestoFormValues = (
  destination: any,
  roomsAndGuests: any,
  dates: any,
  usePoints: any,
  specialRates: any,
  destinationHiddenFields: any
) => {
  const formValues = {
    destination: {
      displayText: destination?.displayText,
      pid: destination?.destinationAddressPlaceId,
    },
    roomsAndGuests: {
      roomsCount: roomsAndGuests?.numRooms,
      adultsCount: roomsAndGuests?.numAdultsPerRoom,
      childrenCount: roomsAndGuests?.numChildrenPerRoom,
      childrenAges: roomsAndGuests?.childrenAges,
    },
    dates: {
      fromDate: dates?.fromDate,
      toDate: dates?.toDate,
      flexible: dates?.flexible,
    },
    usePoints: {
      value: usePoints?.rewardsRedemption,
    },
    specialRates: {
      displayText: specialRates?.clusterCode,
      clusterCode: specialRates?.specialRateCode,
      corporateCode: specialRates?.corporateCode,
      groupCode: specialRates?.groupCode,
    },
    destinationHiddenFields: {
      city: destinationHiddenFields?.city,
      country: destinationHiddenFields?.country,
      latitude: destinationHiddenFields?.latitude,
      longitude: destinationHiddenFields?.longitude,
      state: destinationHiddenFields?.state,
      radius: destinationHiddenFields?.radius,
      region: destinationHiddenFields?.region,
      destinationType: destinationHiddenFields?.destinationType,
    },
    latitudeAndLongitude: {
      latitude: destinationHiddenFields?.latitude,
      longitude: destinationHiddenFields?.longitude,
    },
  };
  return formValues;
};

export const updateRecentSearched = (storeValues: any) => {
  /**
   * recent search item list
   */
  // Parse any JSON previously stored in allEntries
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let existingEntries: any =
    localStorage.getItem('miRecentSearch') !== null ? localStorage.getItem('miRecentSearch') : '[]';
  const fromDate = storeValues?.dates?.fromDate;
  const fromDateUTCString = moment.isMoment(fromDate) ? fromDate?.format('YYYY-MM-DDTHH:mm:ss+0000') : fromDate;
  const toDate = storeValues?.dates?.toDate;
  const toDateUTCString = moment.isMoment(toDate) ? toDate?.format('YYYY-MM-DDTHH:mm:ss+0000') : toDate;
  const entry = {
    label: storeValues?.destination?.displayText ?? '',
    city: storeValues?.destinationHiddenFields?.city ?? '',
    poiname: '',
    state: storeValues?.destinationHiddenFields?.state ?? '',
    country: storeValues?.destinationHiddenFields?.country ?? '',
    stateprovincedisplayname: '',
    destinationType: storeValues?.destinationHiddenFields?.destinationType ?? '',
    countryname: '',
    airportcode: '',
    value: storeValues?.destination?.displayText ?? '',
    geocode:
      storeValues?.destinationHiddenFields?.latitude && storeValues?.destinationHiddenFields?.longitude
        ? `${storeValues?.destinationHiddenFields?.latitude},${storeValues?.destinationHiddenFields?.longitude}`
        : '',
    searchtype: 'recent',
    analytics: '{"location":"searchForm","sendNow":"true","description":"Recent Search"}',
    placeId: storeValues?.destination?.pid || storeValues?.destinationHiddenFields?.placeId,
    primaryDescription: '',
    secondaryDescription: storeValues?.destination?.secondaryDescription ?? '',
    latitude: storeValues?.destinationHiddenFields?.latitude ?? '',
    longitude: storeValues?.destinationHiddenFields?.longitude ?? '',
    radius: storeValues?.destinationHiddenFields?.radius ?? '',
    address: '',
    postalCode: '',
    types: '',
    website: '',
    flexible: storeValues?.dates?.flexible,
    lengthOfStay:
      storeValues?.dates?.lengthOfStay ?? storeValues?.dates?.toDate?.diff(storeValues?.dates?.fromDate, 'days'),
    fromDate: fromDateUTCString,
    toDate: toDateUTCString,
    guests:
      storeValues &&
      storeValues.roomsAndGuests &&
      storeValues.roomsAndGuests.numChildrenPerRoom &&
      storeValues.roomsAndGuests.numAdultsPerRoom
        ? storeValues?.roomsAndGuests?.numChildrenPerRoom + storeValues?.roomsAndGuests?.numAdultsPerRoom
        : storeValues?.roomsAndGuests?.numAdultsPerRoom,
    numberOfAdults: storeValues?.roomsAndGuests?.numAdultsPerRoom,
    numberOfChildren: storeValues?.roomsAndGuests?.numChildrenPerRoom,
    roomsCount: storeValues?.roomsAndGuests?.numRooms,
    childrenAges: storeValues?.roomsAndGuests?.childrenAges,
  };
  existingEntries = JSON.parse(existingEntries);
  if (entry.label !== '') {
    existingEntries.push(entry);
    localStorage.setItem('miRecentSearch', JSON.stringify(existingEntries));
  }
};
/**
 * Extracts the Concur code from the userDetailsState response
 * @param userDetailsState - The user details state response object
 * @returns The concur code (clusterCode) if found, null otherwise
 */
export function extractConcurCode(userDetailsState: any): string | null {
  // Check if customerAffiliates exists and is an array
  if (!userDetailsState?.customerAffiliates || !Array.isArray(userDetailsState.customerAffiliates)) {
    return null;
  }

  // Find the affiliate with CONCUR code
  const concurAffiliate = userDetailsState.customerAffiliates.find(
    (affiliate: any) => affiliate.affiliateCode === CONCUR_AFFILIATE_KEY
  );

  // Check if CONCUR affiliate exists and has account info
  if (
    !concurAffiliate?.accountInfo ||
    !Array.isArray(concurAffiliate.accountInfo) ||
    concurAffiliate.accountInfo.length === 0
  ) {
    return null;
  }

  // Find the account info item with clusterKey="CORPCODE" and return its clusterCode
  const corpCodeAccount = concurAffiliate.accountInfo.find((account: any) => account.clusterKey === 'CORPCODE');

  const clusterCode = corpCodeAccount?.clusterCode?.trim() || null;

  // Suppress concur code when length is less than 2 or greater than 9 characters
  if (clusterCode && (clusterCode.length < 2 || clusterCode.length > 9)) {
    return null;
  }

  return clusterCode;
}

/**
 * Checks if the user has a Concur affiliation
 * @param userDetailsState - The user details state response object
 * @returns True if user has Concur affiliation, false otherwise
 */
export function hasConcurAffiliation(userDetailsState: any): boolean {
  if (!userDetailsState?.customerAffiliates || !Array.isArray(userDetailsState.customerAffiliates)) {
    return false;
  }

  return userDetailsState.customerAffiliates.some((affiliate: any) => affiliate.affiliateCode === CONCUR_AFFILIATE_KEY);
}

/**
 * Determines whether the Concur flow should be active.
 * Both conditions must be true:
 *  1. The feature flag `experience_mcom_shop_enable_concur_flow` is enabled (via envVarsObject.featureFlags)
 *  2. The current session indicates an authenticated user (AriesCommon.memState === 'authenticated')
 *
 * @param featureFlags - The featureFlags object from envVarsObject (Record<string, any>)
 * @param sessionDoc   - The session document object containing AriesCommon (i.e. sessionData.data or sessionData.cacheData.data)
 * @returns true when both the feature flag and authenticated session are present
 */
export function isConcurFlowActive(featureFlags: Record<string, any> | undefined, sessionDoc: any): boolean {
  const CONCUR_FLOW_FLAG = 'experience_mcom_shop_enable_concur_flow';
  if (!featureFlags?.[CONCUR_FLOW_FLAG]) return false;
  const memState = sessionDoc?.AriesCommon?.memState;
  return memState === 'authenticated';
}

/**
 * Determines special rates for search based on priority:
 * 1. Use Points checked - clear special rates
 * 2. User has selected a special rate - use it
 * 3. Concur code available - apply it
 * 4. Default - Lowest Regular Rate
 *
 * @param currentSpecialRateCode - Current special rate code (e.g., 'corp', 'aaa', 'gov', 'none')
 * @param currentCorporateCode - Current corporate/promo code value
 * @param concurCode - Concur corporate code from user details
 * @param usePointsChecked - Whether rewards redemption is active
 * @param specialRatesListItems - List of special rate options for label lookup
 * @returns Resolved special rates object with clusterCode (label), specialRateCode, and corporateCode
 */
export function getSpecialRatesForSearch(
  currentSpecialRateCode: string | null | undefined,
  currentCorporateCode: string | null | undefined,
  concurCode: string | null | undefined,
  usePointsChecked: boolean = false,
  specialRatesListItems: Array<{ specialRatesCode?: string; specialRateslabel?: string }> = []
): { clusterCode: string; specialRateCode: string; corporateCode: string } {
  // Priority 1: Use Points checked - clear special rates
  if (usePointsChecked) {
    return {
      clusterCode: '',
      specialRateCode: 'none',
      corporateCode: '',
    };
  }

  // Priority 2: User has selected a special rate (any non-default value)
  const hasUserSelection = currentSpecialRateCode && currentSpecialRateCode !== 'none';

  if (hasUserSelection) {
    // Find the label for the selected rate code
    const rateItem = specialRatesListItems.find(item => item.specialRatesCode === currentSpecialRateCode);
    const label = rateItem?.specialRateslabel || '';

    return {
      clusterCode: label,
      specialRateCode: currentSpecialRateCode,
      corporateCode: currentCorporateCode || '',
    };
  }

  // Priority 3: Concur code available - apply it
  if (concurCode && concurCode !== 'none' && concurCode !== '') {
    const corpRateItem = specialRatesListItems.find(item => item.specialRatesCode === 'corp');
    const corpLabel = corpRateItem?.specialRateslabel || 'Corp/Promo Code';

    return {
      clusterCode: corpLabel,
      specialRateCode: 'corp',
      corporateCode: concurCode,
    };
  }

  // Priority 4: Default - Lowest Regular Rate
  return {
    clusterCode: '',
    specialRateCode: 'none',
    corporateCode: '',
  };
}

/**
 * sessionStorage key for tracking user override of Concur rates
 */
const CONCUR_USER_OVERRIDE_KEY = '__concurUserOverride';

/**
 * Check if user has manually overridden Concur rates in this session
 * @returns true if user has overridden Concur, false otherwise
 */
export const hasConcurUserOverride = (): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(CONCUR_USER_OVERRIDE_KEY) === 'true';
};

/**
 * Mark that user has overridden Concur rates
 * Sets a sessionStorage flag that persists across page reloads
 * but clears when browser/tab closes
 */
export const setConcurUserOverride = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(CONCUR_USER_OVERRIDE_KEY, 'true');
};

/**
 * Clear the Concur override flag
 * Called on sign-out or when we want to reset the override state
 */
export const clearConcurUserOverride = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(CONCUR_USER_OVERRIDE_KEY);
};

// Increments the search call counter in sessionStorage and updates the URL hash
// to reflect the current search iteration for datalayer tracking purposes.
export const incrementHashCounter = (headersData: any, pageType: 'serp' | 'adf' = 'serp') => {
  const sessionKey = pageType === 'adf' ? 'updateAdfSearchCounter' : 'updateSearchCounter';
  let localSearchCounterValue;
  if (sessionStorage.getItem(sessionKey)) {
    const sessionValue = sessionStorage.getItem(sessionKey);
    sessionStorage.setItem(sessionKey, (parseInt(sessionValue as string) + 1).toString());
    localSearchCounterValue = (parseInt(sessionValue as string) + 1).toString();
  } else {
    sessionStorage.setItem(sessionKey, '0');
    localSearchCounterValue = '0';
  }
  if (window && window.dataLayer) {
    window.dataLayer['updates_search_call_counter'] = localSearchCounterValue;
  }
  // Update the window.location.hash with the update search counter
  window.history.replaceState({}, headersData?.referer as string, `#/${localSearchCounterValue}/`);
};

/**
 * Fires a datalayer tracking call when the HQV model.json call falls back
 * to a sessionId retrieved from the cookie instead of the session token from sessionData.
 * This ensures analytics correctly captures the fallback scenario by sending the
 * search_fallback_exists / search_fallback_type flags to the datalayer API.
 */
export async function callDataLayerForHQVSessionIdFallback(
  resolvedUrl: any,
  headersData: any,
  datalayerParams: any,
  sessionID: string,
  currentLocale?: string,
  isSearchFallback?: boolean,
  searchFallbackType?: string
): Promise<void | unknown> {
  const getPageURI = resolvedUrl ? resolvedUrl.split('?')[0] : '';
  const absolutePageURL = `${getPageURI}.mi`;
  const localeKeyValue = currentLocale?.replace('_', '-') ?? 'en-US';
  const channelValue = datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME?.split('.')[0];
  const datalayerUrl = datalayerParams?.MI_DATALAYER_APP_URL || '';
  try {
    const getHeaders = (): string => {
      let cookieValues = '';
      if (headersData?.mi_site) {
        cookieValues = cookieValues + ` MI_SITE=${headersData?.mi_site};`;
      }
      if (headersData?.mi_visitor) {
        cookieValues = cookieValues + ` MI_VISITOR=${headersData?.mi_visitor};`;
      }
      if (headersData?.remembermeuserid) {
        cookieValues = cookieValues + ` RememberMeUserID=${encodeURIComponent(headersData?.remembermeuserid)};`;
      }
      if (headersData?.remembermealternateid) {
        cookieValues =
          cookieValues + ` RememberMeAlternateID=${encodeURIComponent(headersData?.remembermealternateid)};`;
      }
      if (headersData?.useridtoken) {
        cookieValues = cookieValues + ` UserIdToken=${encodeURIComponent(headersData?.useridtoken)};`;
      }
      return cookieValues;
    };

    const headersKey: Record<string, string | undefined> = {};

    // creating headers for datalayer call
    if (headersData && Object.keys(headersData).length > 0) {
      DATALAYER_CONSTANT.headerKeys.forEach(name => {
        if (name && headersData[name as KeysRequest['keys']]) {
          headersKey[name] = headersData[name as KeysRequest['keys']];
        }
      });
    }

    let acceptLangHeader;
    if (headersKey['accept-language']) {
      acceptLangHeader = headersKey['accept-language'];
      delete headersKey['accept-language'];
    }
    const reqBody: any = {
      context: {
        localeKey: localeKeyValue,
        programFlag: '',
        siteName: datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME,
        mobileAuthEnabled: 'false',
        productSiteId: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        channel: channelValue,
        pageContent: [],
        pageURI: getPageURI,
        referer: headersData?.referer,
        absolutePageURL: absolutePageURL,
        applicationName: datalayerParams?.NEXT_PUBLIC_DATALAYER_APPLICATION,
        products: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        template: datalayerParams?.NEXT_PUBLIC_DATALAYER_TEMPLATE,
        isDtt: true,
        isSearchFallback: isSearchFallback,
        searchFallbackType: searchFallbackType,
      },
      sessionToken: sessionID,
      sourceURI: absolutePageURL,
      variation: datalayerParams?.NEXT_PUBLIC_DATALAYER_VARIATION,
    };
    // datalayer call
    const dataLayer = await Axios.post(datalayerUrl, reqBody, {
      headers: {
        // please make sure these coookies are in single line. Else there will be `TypeError [ERR_INVALID_CHAR]`
        cookie: `sessionID=${sessionID}; RememberMeFlag=${headersData?.remembermeflag}; ${getHeaders()}`,
        'Accept-Language': getLanguage() || acceptLangHeader || 'en-US',
        ...headersKey,
      },
    });
    const datalayerResponse = dataLayer?.data?.component?.data?.dataProperties;

    if (datalayerResponse?.length && window?.dataLayer) {
      for (const [key] of Object.entries(window.dataLayer)) {
        datalayerResponse?.forEach((entry: any) => {
          if (window.dataLayer) {
            if (key === entry.key) {
              window.dataLayer[key] = entry.value;
            } else {
              window.dataLayer[entry.key] = entry.value;
            }
          }
        });
      }
    }
    const isADF = resolvedUrl?.includes('/availabilityCalendar') ? true : false;
    incrementHashCounter(headersData, isADF ? 'adf' : 'serp');
    return; // Explicit return for success case
  } catch (error) {
    return error;
  }
}

export const getSearchFormClickTrackingLoc = (
  isNextGenSearchForm?: boolean,
  isBaselineSearchForm?: boolean
): string => {
  if (isNextGenSearchForm) {
    return nextGenSearchFormClickTrackingLoc;
  } else if (isBaselineSearchForm) {
    return baselineSearchFormClickTrackingLoc;
  } else {
    return searchFormClickTrackingLoc;
  }
};
